import { ffhClient } from './ffh-client';

export const submitFunnelEmailCapture = async (data: {
  email: string;
  funnelSlug: string;
  funnelVariant?: string;
}) => {
  const { error } = await ffhClient().POST('/funnels/email-capture', {
    // Trimmed defensively: type="email" inputs sanitise whitespace in most
    // browsers, but not reliably in every webview, and the profileId the
    // backend derives from this email must match what Auth0's (trimmed)
    // signup form produces later.
    body: { ...data, email: data.email.trim() },
    credentials: 'include'
  });

  if (error) {
    const errorBody = error as { error?: { message?: string } } | undefined;
    const detail = errorBody?.error?.message;
    throw new Error(
      detail ? `Failed to capture email: ${detail}` : 'Failed to capture email'
    );
  }
};

export const submitFunnelResultSnapshot = async (
  funnelSlug: string,
  resultSnapshot: Record<string, unknown>,
  options?: {
    /**
     * Whether part of the result is still being solved for.
     *
     * The completion event cannot be amended once sent and re-sending it counts
     * the funnel twice, so a result that is not finished asks the backend to
     * hold the completion rather than announce a partial one. It is then sent
     * by whoever finishes the result. See ffh-public-api#569.
     */
    isCompletionDeferred?: boolean;
    /**
     * The address this funnel captured, as the fallback identity.
     *
     * The backend finds the capture by the `_funnel_session` cookie, and this
     * is what it falls back to when that cookie did not travel. Without it a
     * cookie-less completion 404s and can never be reported by anything — not
     * even the backend's own sweep. See FFH-2376.
     */
    email?: string;
  }
) => {
  // Trimmed before the emptiness test, not after it: a whitespace-only value is
  // truthy, so testing first would send `email: ''` and the backend would reject
  // the whole request — losing the completion over a field that is optional.
  const email = options?.email?.trim();

  const { error } = await ffhClient().PATCH(
    '/funnels/email-capture/{funnelSlug}/result',
    {
      params: { path: { funnelSlug } },
      body: {
        resultSnapshot,
        ...(options?.isCompletionDeferred && { isCompletionDeferred: true }),
        ...(email && { email })
      },
      credentials: 'include'
    }
  );

  if (error) {
    const errorBody = error as { error?: { message?: string } } | undefined;
    const detail = errorBody?.error?.message;
    throw new Error(
      detail
        ? `Failed to submit result snapshot: ${detail}`
        : 'Failed to submit result snapshot'
    );
  }
};

/** How long to wait before the single retry of a failed completion report. */
const COMPLETION_RETRY_DELAY_MS = 1000;

/**
 * Reports a funnel completion, and complains if it cannot.
 *
 * Not awaited by its callers: the visitor's reveal does not wait on reporting.
 * But it is no longer discarded either. This is the only signal that a visitor
 * finished the funnel, the request that carries it is the one thing standing
 * between a completion and CleverTap never hearing about it, and it used to
 * fail silently — which is how a funnel can look perfect while its numbers
 * halve (FFH-2376).
 *
 * Retried once, because the common failures are transient: the capture that
 * issues the session cookie may still have been in flight, or the network
 * blipped mid-reveal. A second failure is logged and given up on; the backend's
 * sweep is the last line for anything it managed to record.
 */
export const reportFunnelCompletion = ({
  funnelSlug,
  resultSnapshot,
  email,
  isCompletionDeferred
}: {
  funnelSlug: string;
  resultSnapshot: Record<string, unknown>;
  email?: string;
  isCompletionDeferred?: boolean;
}): void => {
  const submit = async () =>
    submitFunnelResultSnapshot(funnelSlug, resultSnapshot, {
      isCompletionDeferred,
      email
    });

  void submit().catch(async (firstError: unknown) => {
    await new Promise((resolve) => {
      setTimeout(resolve, COMPLETION_RETRY_DELAY_MS);
    });

    return submit().catch((retryError: unknown) => {
      // eslint-disable-next-line no-console
      console.error('Funnel completion could not be reported', {
        funnelSlug,
        firstError,
        retryError
      });
    });
  });
};
